/**
 * The learner's desk at `/desk` (docs/server-workspace.md §4): the client for
 * `/api/workspace`, where the learner's agent works on our servers and the
 * learner holds the ticket and presses Check my work (owner, 2026-10-30).
 *
 * Every answer is checked here the way lib/efficiency.ts checks the meter:
 * the Worker may be a deploy ahead of or behind this bundle, and a field that
 * does not read is left out rather than guessed. A read has more than one
 * failure and they are kept apart, because the page says something different
 * for each: signed out, server workspaces off here, the allowance spent, a
 * refusal of the task, and "could not ask".
 */
import type { BridgeEntry } from './agent-bridge';
import type { AgentDid } from './strings/agents';
import { agentCall } from '../components/agent/AgentKeys';

/* ── The address ────────────────────────────────────────────────────────── */

export const DESK_PATH = '/desk';

/**
 * Is this the desk's address? Read in main.tsx before React mounts: the desk
 * has no machine in it, and `App` would boot one.
 */
export function isDeskPath(pathname: string): boolean {
  return pathname === DESK_PATH || pathname === `${DESK_PATH}/`;
}

/* ── The shapes, as docs/server-workspace.md §4 states them ─────────────── */

export interface DeskMission {
  id: string;
  title: string;
  folder: string;
  /** Seeded: the brief as this workspace's seed wrote it, not the catalogue's template. */
  brief: string;
}

export interface DeskState {
  configured: boolean;
  /** A course tab is lent to the agent: its calls go there, not to this workspace. */
  connected: boolean;
  mission: DeskMission | null;
  /** The workspace is awake and seeded; false while it sleeps or restores. */
  ready: boolean;
  lastCheck: DeskCheck | null;
  /** Newest first, whatever order the Worker kept them in. */
  log: BridgeEntry[];
  /** Awake minutes left this month, or null when the Worker does not say. */
  minutesLeft: number | null;
}

export type DeskProblem = 'sign-in' | 'off' | 'refused' | 'spent' | 'other';
export type DeskRead = { ok: true; state: DeskState } | { ok: false; error: DeskProblem };
/** One objective's result as the Worker sends it (worker/workspace-host.ts `CheckMark`), the shape the Objectives list ticks from. */
export interface DeskMark {
  id: string;
  title: string;
  passed: boolean;
  hidden: boolean;
  skipped?: boolean;
  message?: string;
}

/** A check: `at` is when it was graded (0 when the Worker did not say), `marks` empty on a check stored before marks existed. */
export interface DeskCheck {
  passed: boolean;
  summary: string;
  at: number;
  marks: DeskMark[];
}

export type CheckRead = ({ ok: true } & DeskCheck) | { ok: false; error: DeskProblem };

const LOG_SHOWN = 50;

function readEntry(raw: unknown): BridgeEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const e = raw as Record<string, unknown>;
  if (typeof e.at !== 'number' || typeof e.what !== 'string') return null;
  return { at: e.at, what: e.what as AgentDid, detail: typeof e.detail === 'string' ? e.detail : '', failed: e.failed === true };
}

function readMark(raw: unknown): DeskMark | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  if (typeof m.id !== 'string' || typeof m.passed !== 'boolean') return null;
  return {
    id: m.id,
    title: typeof m.title === 'string' ? m.title : m.id,
    passed: m.passed,
    hidden: m.hidden === true,
    ...(m.skipped === true ? { skipped: true } : {}),
    ...(typeof m.message === 'string' ? { message: m.message } : {}),
  };
}

function readCheck(raw: unknown): DeskCheck | null {
  if (!raw || typeof raw !== 'object') return null;
  const c = raw as Record<string, unknown>;
  if (typeof c.passed !== 'boolean') return null;
  return {
    passed: c.passed,
    summary: typeof c.summary === 'string' ? c.summary : '',
    at: typeof c.at === 'number' && Number.isFinite(c.at) ? c.at : 0,
    marks: Array.isArray(c.marks) ? c.marks.map(readMark).filter((m): m is DeskMark => m !== null) : [],
  };
}

/**
 * What the verdict line says beside "Passed." or "Not yet.": the summary's
 * own sentences without that same word (the Worker's summary starts with it)
 * and without its per-objective lines, which the ticked list shows. With no
 * marks to tick (a check stored before them) those lines are all there is,
 * so they come back as `lines`.
 */
export function verdictText(check: DeskCheck): { sentence: string; lines: string[] } {
  const all = check.summary.split('\n').map((l) => l.trim()).filter(Boolean);
  const sentence = all.filter((l) => !l.startsWith('- ')).join(' ').replace(/^(Passed|Not yet)\.\s*/, '');
  const lines = check.marks.length > 0 ? [] : all.filter((l) => l.startsWith('- ')).map((l) => l.slice(2));
  return { sentence, lines };
}

export function readDesk(raw: unknown): DeskState | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const m = d.mission as Record<string, unknown> | null | undefined;
  const mission = m && typeof m === 'object' && typeof m.id === 'string'
    ? { id: m.id, title: typeof m.title === 'string' ? m.title : m.id, folder: typeof m.folder === 'string' ? m.folder : '', brief: typeof m.brief === 'string' ? m.brief : '' }
    : null;
  const log = Array.isArray(d.log) ? d.log.map(readEntry).filter((e): e is BridgeEntry => e !== null) : [];
  return {
    // Anything but a literal true is off: a desk that offered a workspace the Worker does not run would fail on every button.
    configured: d.configured === true,
    connected: d.connected === true,
    mission,
    ready: d.ready === true,
    lastCheck: readCheck(d.lastCheck),
    log: log.sort((a, b) => b.at - a.at).slice(0, LOG_SHOWN),
    minutesLeft: typeof d.minutesLeft === 'number' && Number.isFinite(d.minutesLeft) ? Math.max(0, d.minutesLeft) : null,
  };
}

/**
 * A refusal, by status and by the Worker's `error` word where it gives one.
 * 404 is the route not being there, which is server workspaces off on this
 * deployment; the allowance is named by word because its status is the
 * Worker's choice (429 or 402).
 */
function problemFor(status: number, data: Record<string, unknown> | null): DeskProblem {
  const word = typeof data?.error === 'string' ? data.error : '';
  if (status === 401) return 'sign-in';
  if (status === 404) return 'off';
  if (status === 429 || status === 402 || /allowance|minutes/.test(word)) return 'spent';
  if (status === 403 || status === 409) return 'refused';
  return 'other';
}

async function deskCall(path: string, body?: unknown): Promise<DeskRead> {
  const { status, data } = await agentCall(path, body);
  if (status !== 200) return { ok: false, error: problemFor(status, data) };
  const state = readDesk(data);
  return state ? { ok: true, state } : { ok: false, error: 'other' };
}

/** GET /api/workspace. */
export function fetchDesk(): Promise<DeskRead> {
  return deskCall('/api/workspace');
}

/** POST /api/workspace/open: wipe, seed, a new measured run. Gated on the Worker as the map gates. */
export function openDeskTask(id: string): Promise<DeskRead> {
  return deskCall('/api/workspace/open', { id });
}

/** POST /api/workspace/reset: the same task from a fresh seed. */
export function resetDeskTask(): Promise<DeskRead> {
  return deskCall('/api/workspace/reset', {});
}

/** POST /api/workspace/check: the learner's own button. */
export async function checkDeskWork(): Promise<CheckRead> {
  const { status, data } = await agentCall('/api/workspace/check', {});
  if (status !== 200) return { ok: false, error: problemFor(status, data) };
  const check = readCheck(data);
  return check ? { ok: true, ...check } : { ok: false, error: 'other' };
}

/**
 * Whether an agent has ever reached this account: a live key that has been
 * used (an OAuth sign-in is an agent key too, §5). Null when the keys could
 * not be read, which the page treats as "do not nag".
 */
export async function agentEverConnected(): Promise<boolean | null> {
  const { status, data } = await agentCall('/api/agent/keys');
  if (status !== 200 || !Array.isArray(data?.keys)) return null;
  return (data.keys as Record<string, unknown>[]).some((k) => k && !k.revokedAt && typeof k.lastUsedAt === 'number');
}
